// src/components/Sidebar/Sidebar.js
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AiOutlineHome,
  AiOutlineShoppingCart,
  AiOutlineUser,
  AiOutlineShop,
  AiOutlineDollar,
  AiOutlineMenu,
  AiOutlineClose,
  AiOutlineStar,
  AiOutlineFundProjectionScreen,
  AiOutlineAppstore,
  AiOutlineBarChart,
  AiOutlineCalculator,
  AiOutlineLineChart,
  AiOutlineExperiment,
  AiOutlineSearch,
  AiOutlinePicture,
  AiOutlineCalendar,
  AiOutlineInbox,
  AiOutlineBarcode,
  AiOutlineFileText,
  AiOutlineGift,
  AiOutlineDown
} from 'react-icons/ai';
import './Sidebar.css';

// 메뉴 그룹 정의 — 항목 추가/이동은 이 배열만 수정하면 됩니다.
const MENU_GROUPS = [
  {
    title: '상품·매입',
    icon: AiOutlineShop,
    items: [
      { to: '/admin/products/add', label: '상품추가', icon: AiOutlineShoppingCart },
      { to: '/admin/products/quick-add', label: '빠른등록', icon: AiOutlineShoppingCart },
      { to: '/admin/products', label: '상품정보', icon: AiOutlineShop },
      { to: '/admin/price-setting', label: '가격설정', icon: AiOutlineDollar },
      { to: '/admin/shopping-list', label: '매입 쇼핑목록', icon: AiOutlineShoppingCart },
      { to: '/admin/hs-codes', label: 'HS코드 관리', icon: AiOutlineBarcode },
    ],
  },
  {
    title: '주문·고객',
    icon: AiOutlineInbox,
    items: [
      { to: '/admin/orders', label: '주문', icon: AiOutlineDollar },
      { to: '/admin/product-requests', label: '상품 요청', icon: AiOutlineInbox },
      { to: '/admin/pickups', label: '픽업 관리', icon: AiOutlineCalendar },
      { to: '/admin/customers', label: '고객', icon: AiOutlineUser },
    ],
  },
  {
    title: '재고',
    icon: AiOutlineBarChart,
    items: [
      { to: '/admin/product-analytics', label: '주문량/재고', icon: AiOutlineBarChart },
      { to: '/admin/inventory', label: '보유재고 관리', icon: AiOutlineInbox },
    ],
  },
  {
    title: '정산·수익',
    icon: AiOutlineLineChart,
    items: [
      { to: '/admin/profit', label: '수익분석', icon: AiOutlineLineChart },
      { to: '/admin/settlements', label: '정산/금액정리', icon: AiOutlineDollar },
      { to: '/admin/pricing', label: '손익시뮬레이터', icon: AiOutlineExperiment },
      { to: '/admin/price-compare', label: '가격비교', icon: AiOutlineSearch },
      { to: '/admin/tools', label: '계산도구', icon: AiOutlineCalculator },
      { to: '/admin/similarity', label: '소비유사도', icon: AiOutlineFundProjectionScreen },
    ],
  },
  {
    title: '사이트 노출',
    icon: AiOutlineAppstore,
    items: [
      { to: '/admin/recommends', label: '추천관리', icon: AiOutlineStar },
      { to: '/admin/themes', label: '테마관리', icon: AiOutlineAppstore },
      { to: '/admin/banners', label: '배너관리', icon: AiOutlinePicture },
      { to: '/admin/category-icons', label: '카테고리 아이콘', icon: AiOutlinePicture },
      { to: '/admin/home-sections', label: '홈 섹션 정렬', icon: AiOutlinePicture },
      { to: '/admin/coupons', label: '쿠폰관리', icon: AiOutlineGift },
      { to: '/admin/loyalty-grants', label: '포인트 지급 규칙', icon: AiOutlineGift },
    ],
  },
];

// 그룹에 속하지 않는 단독 항목
const STANDALONE_ITEMS = [
  { to: '/admin/ops-guide', label: '운영 가이드', icon: AiOutlineFileText },
  { to: '/admin/system-events', label: '운영 추적', icon: AiOutlineFileText },
];

// 현재 경로가 속한 그룹 제목 찾기
const findActiveGroupTitle = (pathname) => {
  const group = MENU_GROUPS.find((g) => g.items.some((i) => i.to === pathname));
  return group ? group.title : null;
};

function Sidebar() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  // 펼쳐진 그룹 상태 — 현재 경로가 속한 그룹은 기본으로 펼침
  const [openGroups, setOpenGroups] = useState(() => {
    const active = findActiveGroupTitle(location.pathname);
    return active ? { [active]: true } : {};
  });

  // 현재 경로와 메뉴 항목의 경로가 일치하는지 확인
  const isActive = (path) => {
    return location.pathname === path;
  };

  // 그룹 접기/펼치기
  const toggleGroup = (title) => {
    setOpenGroups((prev) => ({ ...prev, [title]: !prev[title] }));
  };

  // 모바일에서 메뉴 항목 클릭시 사이드바 닫기
  const handleMenuClick = () => {
    if (window.innerWidth <= 768) {
      setIsOpen(false);
    }
  };

  // 경로 변경 시 해당 그룹 자동 펼침
  useEffect(() => {
    const active = findActiveGroupTitle(location.pathname);
    if (active) {
      setOpenGroups((prev) => (prev[active] ? prev : { ...prev, [active]: true }));
    }
  }, [location.pathname]);

  // ESC 키로 사이드바 닫기
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.keyCode === 27) {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, []);

  // 화면 크기 변경 시 사이드바 상태 관리
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <>
      {/* 모바일 메뉴 토글 버튼 */}
      <button className="menu-toggle" onClick={() => setIsOpen(!isOpen)}>
        {isOpen ? <AiOutlineClose /> : <AiOutlineMenu />}
      </button>

      {/* 모바일 오버레이 */}
      <div
        className={`sidebar-overlay ${isOpen ? 'show' : ''}`}
        onClick={() => setIsOpen(false)}
      />

      {/* 사이드바 */}
      <div className={`sidebar ${isOpen ? 'show' : ''}`}>
        <div className="sidebar-header">
          <Link to="/admin" className="sidebar-home" onClick={handleMenuClick}>
            <AiOutlineHome size={20} className="me-2" />
            <span>TransBridge</span>
          </Link>
        </div>

        <small className="section-title px-3">메뉴</small>

        <ul className="nav flex-column sidebar-menu">
          {MENU_GROUPS.map((group) => {
            const GroupIcon = group.icon;
            const opened = !!openGroups[group.title];
            const hasActive = group.items.some((i) => isActive(i.to));
            return (
              <li className="nav-group" key={group.title}>
                <button
                  type="button"
                  className={`nav-group-header ${opened ? 'open' : ''} ${hasActive ? 'has-active' : ''}`}
                  onClick={() => toggleGroup(group.title)}
                  aria-expanded={opened}
                >
                  <span className="nav-group-title">
                    <GroupIcon size={20} className="me-2" />
                    {group.title}
                  </span>
                  <AiOutlineDown size={12} className="nav-group-chevron" />
                </button>
                <ul className={`nav-group-items ${opened ? 'open' : ''}`}>
                  {group.items.map((item) => {
                    const ItemIcon = item.icon;
                    return (
                      <li className="nav-item" key={item.to}>
                        <Link
                          to={item.to}
                          className={`nav-link ${isActive(item.to) ? 'active' : ''}`}
                          onClick={handleMenuClick}
                        >
                          <ItemIcon size={20} className="me-2" />
                          {item.label}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </li>
            );
          })}

          {STANDALONE_ITEMS.map((item) => {
            const ItemIcon = item.icon;
            return (
              <li className="nav-item" key={item.to}>
                <Link
                  to={item.to}
                  className={`nav-link ${isActive(item.to) ? 'active' : ''}`}
                  onClick={handleMenuClick}
                >
                  <ItemIcon size={20} className="me-2" />
                  {item.label}
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </>
  );
}

export default Sidebar;
